'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

export type GraftcodeOnboardingStepMotion = 'forward' | 'back' | 'swap' | null

const MOTION_CLEAR_MS = 480

export function getGraftcodeOnboardingStepElementId(stepIndex: number): string {
  return `graftcode-onboarding-step-${stepIndex}`
}

type UseGraftcodeOnboardingGuideMotionOptions = {
  activeStepIndex: number
  stepsReady: boolean
  configKey: string
  contentRefreshing: boolean
  /** Skip staggered page enter (wizard ? guide handoff with prefetched content). */
  instantEnter?: boolean
}

export function useGraftcodeOnboardingGuideMotion({
  activeStepIndex,
  stepsReady,
  configKey,
  contentRefreshing,
  instantEnter = false,
}: UseGraftcodeOnboardingGuideMotionOptions) {
  const [stepMotion, setStepMotion] = useState<GraftcodeOnboardingStepMotion>(null)
  const [guideEnter, setGuideEnter] = useState(false)
  const prevConfigKeyRef = useRef<string | null>(null)
  const pendingConfigSwapRef = useRef(false)
  const prevStepIndexRef = useRef(activeStepIndex)
  const motionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleMotionClear = useCallback(() => {
    if (motionTimerRef.current) {
      clearTimeout(motionTimerRef.current)
    }
    motionTimerRef.current = setTimeout(() => setStepMotion(null), MOTION_CLEAR_MS)
  }, [])

  const beginStepForward = useCallback(() => {
    setStepMotion('forward')
    scheduleMotionClear()
  }, [scheduleMotionClear])

  const beginStepBack = useCallback(() => {
    setStepMotion('back')
    scheduleMotionClear()
  }, [scheduleMotionClear])

  useEffect(() => {
    if (prevConfigKeyRef.current === null) {
      prevConfigKeyRef.current = configKey
      prevStepIndexRef.current = activeStepIndex
      return
    }

    if (prevConfigKeyRef.current !== configKey) {
      prevConfigKeyRef.current = configKey
      pendingConfigSwapRef.current = true
      prevStepIndexRef.current = activeStepIndex
      return
    }
  }, [configKey, activeStepIndex])

  useEffect(() => {
    if (!stepsReady || contentRefreshing || !pendingConfigSwapRef.current) {
      return
    }

    pendingConfigSwapRef.current = false
    setStepMotion('swap')
    scheduleMotionClear()
  }, [stepsReady, contentRefreshing, configKey, scheduleMotionClear])

  useEffect(() => {
    if (stepsReady && !contentRefreshing && !guideEnter) {
      setGuideEnter(true)
    }
  }, [stepsReady, contentRefreshing, guideEnter])

  useEffect(() => {
    if (!stepsReady || contentRefreshing || pendingConfigSwapRef.current) {
      return
    }

    if (prevStepIndexRef.current === activeStepIndex) {
      return
    }

    const stepChangedByUser = Math.abs(prevStepIndexRef.current - activeStepIndex) === 1
    prevStepIndexRef.current = activeStepIndex

    if (!stepChangedByUser) {
      return
    }

    const elementId = getGraftcodeOnboardingStepElementId(activeStepIndex)
    requestAnimationFrame(() => {
      document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [activeStepIndex, stepsReady, contentRefreshing])

  useEffect(
    () => () => {
      if (motionTimerRef.current) {
        clearTimeout(motionTimerRef.current)
      }
    },
    []
  )

  return {
    stepMotion,
    guideEnter,
    guideEnterInstant: guideEnter && instantEnter,
    beginStepForward,
    beginStepBack,
  }
}
